/**
 * AttemptDetailModal — reopen the FULL screener behind a past determination.
 *
 * Clicking a row in the Screening history loads the attempt's pinned prescreen
 * version (GET /api/crm/attempts/:id — the exact criteria it was screened
 * against, not the study's current ones) and lays every criterion out as a clean
 * question → answer grid (the shared ReviewAnswerGrid, styled after the WCR
 * Health-History PDF). This is a REVIEW surface, not the runner: no data-entry
 * widgets, so the determination reads at a glance. The outcome is in the header
 * badge, disqualifying answers are flagged red, and the recorded DQ note (if any)
 * sits right above the grid.
 */
import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import api from '../../../services/api';
import { PrescreenCriterion, StudyAttempt } from '../../types';
import {
  answerValue, attemptStatusMeta, DQ_CATEGORIES, evalCriterionRule,
  formatCriterionAnswer, relTime, resolveCalculations,
} from '../../lib';
import ReviewAnswerGrid, { ReviewItem } from '../ReviewAnswerGrid';
import { Alert, Z } from '../ui';

interface AttemptDetailModalProps {
  attemptId: string;
  /** Study title for the header before the fetch resolves. */
  studyTitle?: string | null;
  onClose: () => void;
}

const dqCategoryLabel = (value: string) =>
  DQ_CATEGORIES.find((c) => c.value === value)?.label || value.replace(/_/g, ' ');

export default function AttemptDetailModal({ attemptId, studyTitle, onClose }: AttemptDetailModalProps) {
  const [attempt, setAttempt] = useState<StudyAttempt | null>(null);
  const [criteria, setCriteria] = useState<PrescreenCriterion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/api/crm/attempts/${attemptId}`);
        if (!alive) return;
        setAttempt(res.data?.attempt || null);
        setCriteria(res.data?.criteria || []);
      } catch (err: any) {
        if (alive) setError(err?.response?.data?.error || 'Could not load this screener');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [attemptId]);

  // answerValue() folds the typed columns back into the plain value the renderer
  // and rule evaluator expect, keyed by question. resolveCalculations then fills
  // in the derived age/BMI values so calc rows replay with their verdict.
  const rawAnswers: Record<string, any> = {};
  for (const a of attempt?.answers || []) rawAnswers[a.question_key] = answerValue(a);
  const answersByKey = resolveCalculations(criteria, rawAnswers);

  // Verdict per criterion, computed once — a failing answer gets the red pill.
  const verdicts = criteria.map((c) => evalCriterionRule(c.rule, answersByKey[c.question_key]));

  const title = attempt?.study_title || studyTitle || 'Study';
  const meta = attemptStatusMeta(attempt?.status);
  const dq = attempt?.disqualification || null;
  const landing = attempt?.landing_answers || [];

  // Normalize each surface into the shared review grid's item shape (question →
  // answer, fails flagged red). The outcome lives in the header badge + the DQ
  // banner, so the grid itself stays clean — no per-row chips, no tally.
  const criteriaItems: ReviewItem[] = criteria.map((c, i) => ({
    key: c.id,
    label: c.display_question,
    value: formatCriterionAnswer(c, answersByKey[c.question_key]),
    highlight: verdicts[i] === 'fail' ? 'exclude' : null,
  }));
  // Fallback when the pinned criteria are gone (rare) — the raw recorded answers,
  // so the determination is never a black box.
  const fallbackItems: ReviewItem[] = (attempt?.answers || []).map((a) => ({
    key: a.question_key,
    label: a.question_key,
    value: formatCriterionAnswer({ answer_type: a.question_type || 'text' }, answerValue(a)),
    highlight: null,
  }));
  const landingItems: ReviewItem[] = landing.map((a, i) => ({
    key: `${a.question}-${i}`,
    label: a.question,
    value: a.answer,
    highlight: a.kind === 'exclude' ? 'exclude' : a.kind === 'review' ? 'review' : null,
  }));

  return (
    <div
      className={`fixed inset-0 ${Z.modalRaised} flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4`}
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-t-xl bg-white shadow-xl focus:outline-none dark:bg-gray-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Screener detail"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-2 border-b border-gray-200 px-5 py-3 dark:border-gray-700">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">Screener · {title}</h3>
            {attempt && (
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-400">
                <span className={`rounded-full px-2 py-0.5 font-medium ${meta.badge}`}>{meta.label}</span>
                <span>started {relTime(attempt.started_at)} ago</span>
                {attempt.outcome_at && <span>· closed {relTime(attempt.outcome_at)} ago</span>}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin text-primary-600" /> Loading the screener…
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          ) : (
            <>
              {dq && (
                <Alert tone="error" icon={false}>
                  <span className="font-medium">Did not qualify — {dqCategoryLabel(dq.category)}</span>
                  {dq.note && <span> · {dq.note}</span>}
                </Alert>
              )}

              {criteria.length > 0 ? (
                <ReviewAnswerGrid items={criteriaItems} />
              ) : fallbackItems.length > 0 ? (
                <ReviewAnswerGrid items={fallbackItems} />
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No answers were recorded on this attempt.</p>
              )}

              {/* The patient's original landing-form Q&A (study-matched), collapsed
                  by default beneath the criteria. Omitted when there are none. */}
              {landing.length > 0 && (
                <details className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                  <summary className="cursor-pointer select-none text-sm font-medium text-gray-700 dark:text-gray-200">
                    Landing form answers ({landing.length})
                  </summary>
                  <div className="mt-3">
                    <ReviewAnswerGrid items={landingItems} />
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
